import type { AxiosError } from 'axios';
import { useRequest } from 'ahooks';
import { NotificationInstance } from 'antd/es/notification/interface';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import posthog from 'posthog-js';
import ApiUsersManager from 'services/api/ApiUsersManager';
import { updateUserProperties } from 'services/PostHogIdentity';
import { userActions } from 'store/users';

export function handleLicenseCodeError(error: unknown, api: NotificationInstance, t: TFunction): void {
  const maybeAxios = error as AxiosError | undefined;
  const status = maybeAxios?.response?.status;
  const data = (maybeAxios?.response?.data ?? {}) as { code?: string };

  if (status === 403) {
    switch (data.code) {
      case 'invalid_code':
        api.error({
          message: t('settings.license.errors.invalidLicenseCode'),
        });
        return;
      case 'contract_full':
        api.error({
          message: t('settings.license.errors.contractFull'),
        });
        return;
      case 'contract_expired':
        api.error({
          message: t('settings.license.errors.contractExpired'),
        });
        return;
      default:
        break;
    }
  }

  api.error({
    message: t('settings.license.errors.default'),
  });
}

type UseUpdateLicenseCodeOptions = {
  api: NotificationInstance;
  onSuccess?: () => void;
  posthogSource?: string;
};

export function useUpdateLicenseCode({ api, onSuccess, posthogSource }: UseUpdateLicenseCodeOptions) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useRequest(
    (formValues: { licenseCode: string }) => ApiUsersManager.updateLicenseCode(formValues.licenseCode),
    {
      manual: true,
      onSuccess: async () => {
        try {
          const result = await ApiUsersManager.getUsersMe();
          dispatch(userActions.setCurrentUser({ ...result }));
          // The license code is a PostHog person property and a group, so it has
          // to be pushed even though the identity itself has not changed.
          updateUserProperties(result);
        } catch (e) {
          // Best-effort refresh; log but don't block success
          console.error('Failed to refresh current user after license update', e);
        }

        api.success({
          message: t('settings.license.codeChanged'),
        });

        if (posthogSource) {
          posthog.capture('settings.license_code_updated', { source: posthogSource });
        }

        onSuccess?.();
      },
      onError: error => {
        handleLicenseCodeError(error, api, t);
      },
    },
  );
}
